import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { LessonStatus, InformationText, PaginationContainer, PaginationButton, PaginationInfo, ManageButton, LessonBadgeWithIcon } from './shared/CommonElements';
import { getBackNavigationPath, getBackButtonText } from '../utils/adminUtils';
import ConfirmationPopup from './ConfirmationPopup';
import UserPackagePricesPopup from './UserPackagePricesPopup';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;

const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 38px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  z-index: 1000;
  margin: 0 auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: #fff;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 20px;
  position: relative;
  
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
  }
`;

const UserCard = styled.div`
  background: rgba(188, 231, 253, 0.13);
  border: 1px solid #bce7fd;
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const UserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const UserName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  flex: 1;
  min-width: 0;
`;

const RoleBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.role) {
      case 'ADMIN':
        return 'rgba(219, 39, 98, 0.2)';
      case 'USER':
        return 'rgba(76, 175, 80, 0.2)';
      default:
        return 'rgba(158, 158, 158, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.role) {
      case 'ADMIN':
        return '#c2185b';
      case 'USER':
        return '#2e7d32';
      default:
        return '#616161';
    }
  }};
  border: 1px solid ${props => {
    switch (props.role) {
      case 'ADMIN':
        return '#db2762';
      case 'USER':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  }};
`;

const UserDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1rem;
  color: ${theme.colors.primary};
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const UserStatistics = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const StatisticsTitle = styled.h4`
  font-size: 1.1rem;
  color: ${theme.colors.primary};
  margin-bottom: 15px;
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
`;

const StatValue = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.colors.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 20px;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const SkeletonCard = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 24px;
  margin-bottom: 20px;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SkeletonTitle = styled.div`
  height: 24px;
  width: 200px;
  background: #e0e0e0;
  border-radius: 4px;
`;

const SkeletonBadge = styled.div`
  height: 20px;
  width: 80px;
  background: #e0e0e0;
  border-radius: 12px;
`;

const SkeletonDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SkeletonDetailItem = styled.div`
  height: 16px;
  width: 100%;
  background: #e0e0e0;
  border-radius: 4px;
`;

const UserActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SocialPriceButton = styled(ManageButton)`
  background: ${props => props.eligible ? 'linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%)' : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'};
  border: 2px solid ${props => props.eligible ? props.theme.colors.secondary : '#4CAF50'};
  box-shadow: ${props => props.eligible ? '0 2px 8px rgba(219, 39, 98, 0.08)' : '0 2px 8px rgba(76, 175, 80, 0.08)'};
  
  &:hover:not(:disabled) {
    background: ${props => props.eligible ? 'linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%)' : 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)'};
    border: 2px solid ${props => props.eligible ? props.theme.colors.secondary : '#45a049'};
    transform: scale(1.01);
    box-shadow: ${props => props.eligible ? '0 6px 20px rgba(219, 39, 98, 0.4)' : '0 6px 20px rgba(76, 175, 80, 0.4)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #e0e0e0;
    border: 2px solid #bdbdbd;
    color: #9e9e9e;
    box-shadow: none;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const PackagePermissionButton = styled(ManageButton)`
  background: ${props => props.allowed ? 'linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%)' : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'};
  border: 2px solid ${props => props.allowed ? props.theme.colors.secondary : '#4CAF50'};
  box-shadow: ${props => props.allowed ? '0 2px 8px rgba(219, 39, 98, 0.08)' : '0 2px 8px rgba(76, 175, 80, 0.08)'};
  
  &:hover {
    background: ${props => props.allowed ? 'linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%)' : 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)'};
    border: 2px solid ${props => props.allowed ? props.theme.colors.secondary : '#45a049'};
    transform: scale(1.01);
    box-shadow: ${props => props.allowed ? '0 6px 20px rgba(219, 39, 98, 0.4)' : '0 6px 20px rgba(76, 175, 80, 0.4)'};
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const AdjustPricesButton = styled(ManageButton)`
  background: white;
  border: 2px solid ${theme.colors.accent1};
  color: black;
  box-shadow: none;
  
  &:hover:not(:disabled) {
    background: ${theme.colors.accent1};
    color: white;
    box-shadow: none;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #e0e0e0;
    border: 2px solid #bdbdbd;
    color: #9e9e9e;
    box-shadow: none;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.medium};
  z-index: 10;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  z-index: 1000;
  min-width: 250px;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: #015ecc;
  }
  
  &::placeholder {
    color: #999;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const SearchLabel = styled.label`
  font-weight: 600;
  color: ${theme.colors.primary};
  white-space: nowrap;
`;

const ClearSearchButton = styled.button`
  padding: 8px 16px;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  border-radius: ${theme.borderRadius.small};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e0e0e0;
    color: #333;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    margin-top: 8px;
  }
`;

const AllUsersPage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showSocialPriceConfirmation, setShowSocialPriceConfirmation] = useState(false);
  const [showPackagePermissionConfirmation, setShowPackagePermissionConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showPricePopup, setShowPricePopup] = useState(false);
  const [selectedUserForPrices, setSelectedUserForPrices] = useState(null);
  const [userSpecificPrices, setUserSpecificPrices] = useState(new Map()); // Map<username, hasPrices>
  const usersPerPage = 5;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAuthenticated() || !token) return;
    
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const response = await fetch(config.getApiUrl('/api/allusers/all-users'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUsers(data.users || []);
          
          // Fetch user-specific prices for all users
          const pricesMap = new Map();
          for (const user of data.users || []) {
            try {
              const pricesResponse = await fetch(config.getApiUrl(`/api/allusers/user-package-prices/${user.username}`), {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (pricesResponse.ok) {
                const pricesData = await pricesResponse.json();
                const hasPrices = pricesData.packagePrices && Object.keys(pricesData.packagePrices).length > 0;
                pricesMap.set(user.username, hasPrices);
              }
            } catch (err) {
              // Silently fail for individual price checks
            }
          }
          setUserSpecificPrices(pricesMap);
        } else {
          setError('Fehler beim Laden der Benutzer');
        }
      } catch (err) {
        setError('Verbindungsfehler');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token, isAuthenticated, navigate]);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      setCurrentPage(1);
      return;
    }

    const filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.username?.toLowerCase().includes(searchLower) ||
        user.billingName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.firstName?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, users]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht verfügbar';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Ungültiges Datum';
    }
  };

  const renderSkeletonCards = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <SkeletonCard key={index}>
        <SkeletonHeader>
          <SkeletonTitle />
          <SkeletonBadge />
        </SkeletonHeader>
        <SkeletonDetails>
          <SkeletonDetailItem />
          <SkeletonDetailItem />
          <SkeletonDetailItem />
        </SkeletonDetails>
      </SkeletonCard>
    ));
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSocialPriceToggle = async (username, currentStatus) => {
    if (updatingUsers.has(username)) return;
    
    setPendingAction({
      type: 'socialPrice',
      username,
      currentStatus,
      newStatus: !currentStatus
    });
    setShowSocialPriceConfirmation(true);
  };

  const handlePackagePermissionToggle = async (username, currentStatus) => {
    if (updatingUsers.has(username)) return;
    
    setPendingAction({
      type: 'packagePermission',
      username,
      currentStatus,
      newStatus: !currentStatus
    });
    setShowPackagePermissionConfirmation(true);
  };

  const confirmSocialPriceToggle = async () => {
    if (!pendingAction) return;
    
    const { username, newStatus } = pendingAction;
    setUpdatingUsers(prev => new Set(prev).add(username));
    
    try {
      const response = await fetch(config.getApiUrl('/api/allusers/update-social-price-eligibility'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username,
          eligible: newStatus
        })
      });

      if (response.ok) {
        // Update local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.username === username 
              ? { ...user, socialPriceEligible: newStatus }
              : user
          )
        );
      } else {
        setError('Fehler beim Aktualisieren der Sozialtarif-Berechtigung');
      }
    } catch (err) {
      setError('Verbindungsfehler');
      console.error('Error updating social price eligibility:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(username);
        return newSet;
      });
    }
    
    setShowSocialPriceConfirmation(false);
    setPendingAction(null);
  };

  const confirmPackagePermissionToggle = async () => {
    if (!pendingAction) return;
    
    const { username, newStatus } = pendingAction;
    setUpdatingUsers(prev => new Set(prev).add(username));
    
    try {
      const response = await fetch(config.getApiUrl('/api/allusers/update-package-purchase-permission'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username,
          allowed: newStatus
        })
      });

      if (response.ok) {
        // Update local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.username === username 
              ? { ...user, packagePurchaseAllowed: newStatus }
              : user
          )
        );
      } else {
        setError('Fehler beim Aktualisieren der Paketkaufberechtigung');
      }
    } catch (err) {
      setError('Verbindungsfehler');
      console.error('Error updating package purchase permission:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(username);
        return newSet;
      });
    }
    
    setShowPackagePermissionConfirmation(false);
    setPendingAction(null);
  };

  const cancelAction = () => {
    setShowSocialPriceConfirmation(false);
    setShowPackagePermissionConfirmation(false);
    setPendingAction(null);
  };

  const handleAdjustPrices = (user) => {
    setSelectedUserForPrices({
      username: user.username,
      displayName: user.billingName || user.firstName || user.username
    });
    setShowPricePopup(true);
  };

  const handleClosePricePopup = () => {
    setShowPricePopup(false);
    // Delay clearing selectedUserForPrices to allow exit animation to complete (0.25s animation + small buffer)
    setTimeout(() => {
      setSelectedUserForPrices(null);
    }, 300);
    // Refresh user-specific prices after closing popup
    if (selectedUserForPrices) {
      fetch(config.getApiUrl(`/api/allusers/user-package-prices/${selectedUserForPrices.username}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            const hasPrices = data.packagePrices && Object.keys(data.packagePrices).length > 0;
            setUserSpecificPrices(prev => {
              const newMap = new Map(prev);
              newMap.set(selectedUserForPrices.username, hasPrices);
              return newMap;
            });
          }
        })
        .catch(() => {
          // Silently fail
        });
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <ContentContainer>
          {renderSkeletonCards()}
        </ContentContainer>
      </PageLayout>
    );
  }

  if (!users || users.length === 0) {
    return (
      <PageLayout>
        <ContentContainer>
          <EmptyState>
            <EmptyStateIcon>👥</EmptyStateIcon>
            <EmptyStateText>Keine Benutzer gefunden.</EmptyStateText>
          </EmptyState>
          <BackButton onClick={() => navigate(getBackNavigationPath())}>
            ← Zurück zu {getBackButtonText()}
          </BackButton>
        </ContentContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <LessonBadgeWithIcon />
          <MainTitle>Alle Benutzer</MainTitle>
        </HeroTitle>
        <Subtitle>Übersicht aller genehmigten Benutzerkonten</Subtitle>
      </HeroSection>
      
      <ContentContainer>
        <BackButton onClick={() => navigate(getBackNavigationPath())}>
          ← Zurück zu {getBackButtonText()}
        </BackButton>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            border: '1px solid #ffcdd2',
            borderRadius: theme.borderRadius.medium,
            padding: '16px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Search Bar */}
        <SearchContainer>
          <SearchLabel htmlFor="user-search">Suche:</SearchLabel>
          <SearchInput
            id="user-search"
            type="text"
            placeholder="Suchterm eingeben..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </SearchContainer>

        {currentUsers.map((user, index) => (
          <UserCard key={user.username || index}>
            {updatingUsers.has(user.username) && (
              <LoadingOverlay>
                <div>Wird aktualisiert...</div>
              </LoadingOverlay>
            )}
            <UserHeader>
              <UserName>
                {user.billingName || user.firstName + ' (Nachnahme fehlt)'}
              </UserName>
            </UserHeader>
            
            <UserDetails>
              <DetailItem>
                <DetailLabel>Benutzername</DetailLabel>
                <DetailValue>{user.username}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue style={{ wordBreak: 'break-all' }}>{user.email}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Registrierungsdatum</DetailLabel>
                <DetailValue>{formatDate(user.registrationDate)}</DetailValue>
              </DetailItem>
                <DetailItem>
                  <DetailLabel>Förderberechtigt</DetailLabel>
                  <DetailValue>{user.socialPriceEligible ? 'Ja' : 'Nein'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Paketkauf erlaubt</DetailLabel>
                  <DetailValue>{user.packagePurchaseAllowed ? 'Ja' : 'Nein'}</DetailValue>
                </DetailItem>
            </UserDetails>
            
            
            {/* User Statistics Section */}
            {user.statistics && (
              <UserStatistics>
                <StatisticsTitle>Statistiken</StatisticsTitle>
                <StatisticsGrid>
                  <StatItem>
                    <StatLabel>Stunden</StatLabel>
                    <StatValue>{user.statistics.totalBookedLessons}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Pakete</StatLabel>
                    <StatValue>{user.statistics.purchasedPackages}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Ausgaben</StatLabel>
                    <StatValue>{user.statistics.totalSpent}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Ausgaben/Stunde</StatLabel>
                    <StatValue>{user.statistics.costPerLesson}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Stornierung</StatLabel>
                    <StatValue>{user.statistics.cancellationRate} %</StatValue>
                  </StatItem>
                </StatisticsGrid>
              </UserStatistics>
            )}
           
            {/* User Actions Section */}
            <UserActions>
              <SocialPriceButton
                eligible={user.socialPriceEligible}
                onClick={() => handleSocialPriceToggle(user.username, user.socialPriceEligible)}
                disabled={updatingUsers.has(user.username) || userSpecificPrices.get(user.username)}
                title={userSpecificPrices.get(user.username) ? 'Benutzer mit individuellen Preisen kann nicht förderberechtigt sein' : ''}
              >
                {user.socialPriceEligible ? 'Förderber. aufheben' : 'Förderberechtigt setzen'}
              </SocialPriceButton>
              
              <PackagePermissionButton
                allowed={user.packagePurchaseAllowed}
                onClick={() => handlePackagePermissionToggle(user.username, user.packagePurchaseAllowed)}
                disabled={updatingUsers.has(user.username)}
              >
                {user.packagePurchaseAllowed ? 'Paketkauf verbieten' : 'Paketkauf erlauben'}
              </PackagePermissionButton>
              
              <AdjustPricesButton
                onClick={() => handleAdjustPrices(user)}
                disabled={updatingUsers.has(user.username) || user.socialPriceEligible}
                title={user.socialPriceEligible ? 'Förderberechtigte Benutzer können keine individuellen Preise haben' : ''}
              >
                Preise anpassen
              </AdjustPricesButton>
            </UserActions>
          </UserCard>
        ))}
        
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton 
              onClick={handlePreviousPage} 
              disabled={currentPage === 1}
            >
              ←
            </PaginationButton>
            
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
              <PaginationButton
                key={page}
                onClick={() => handlePageChange(page)}
                active={currentPage === page}
              >
                {page}
              </PaginationButton>
            ))}
            
            <PaginationButton 
              onClick={handleNextPage} 
              disabled={currentPage === totalPages}
            >
              →
            </PaginationButton>
          </PaginationContainer>
        )}
      </ContentContainer>

      {/* Confirmation Popups */}
      {showSocialPriceConfirmation && pendingAction && (
        <ConfirmationPopup
          isOpen={showSocialPriceConfirmation}
          onClose={cancelAction}
          onConfirm={confirmSocialPriceToggle}
          title="Sozialtarifberechtigung"
          message={`Möchtest du die Sozialtarifberechtigung für den Benutzer "${pendingAction.username}" wirklich ${pendingAction.newStatus ? 'aktivieren' : 'deaktivieren'}?`}
          confirmText={pendingAction.newStatus ? 'Aktivieren' : 'Deaktivieren'}
          isDestructive={!pendingAction.newStatus}
          cancelText="Abbrechen"
        />
      )}

      {showPackagePermissionConfirmation && pendingAction && (
        <ConfirmationPopup
          isOpen={showPackagePermissionConfirmation}
          onClose={cancelAction}
          onConfirm={confirmPackagePermissionToggle}
          title="Paketkaufberechtigung"
          isDestructive={!pendingAction.newStatus}
          message={`Möchtest du die Paketkaufberechtigung für den Benutzer "${pendingAction.username}" wirklich ${pendingAction.newStatus ? 'erlauben' : 'verbieten'}?`}
          confirmText={pendingAction.newStatus ? 'Erlauben' : 'Verbieten'}
          cancelText="Abbrechen"
        />
      )}

      {selectedUserForPrices && (
        <UserPackagePricesPopup
          isOpen={showPricePopup}
          onClose={handleClosePricePopup}
          username={selectedUserForPrices.username}
          userDisplayName={selectedUserForPrices.displayName}
        />
      )}
    </PageLayout>
  );
};

export default AllUsersPage;
